import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  FiArrowLeft,
  FiEdit,
  FiTrash2,
  FiBook,
  FiDollarSign,
  FiClock,
  FiUsers,
  FiLayers,
  FiTrendingUp,
  FiCopy,
  FiFileText,
  FiStar,
  FiImage,
  FiX,
  FiPlus,
  FiBarChart2,
  FiActivity,
} from "react-icons/fi";
import adminApi from "../../api/adminApi";
import StatusBadge from "../../components/StatusBadge/StatusBadge";
import Modal from "../../components/Modal/Modal";
import Button from "../../components/Button/Button";
import FormInput from "../../components/FormInput/FormInput";
import Select from "../../components/Select/Select";
import Table from "../../components/Table/Table";
import "./CourseDetail.css";

const CourseDetail = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showModuleModal, setShowModuleModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("overview"); // overview, modules, analytics, reviews
  const [modules, setModules] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    price: "",
    duration: "",
    level: "",
    status: "active",
    courseSubheading: "",
    features: [],
    batchSize: "",
    lessonsCount: "",
    durationHours: "",
  });

  useEffect(() => {
    loadCourse();
    loadModules();
    loadAnalytics();
    loadReviews();
  }, [courseId]);

  const loadCourse = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminApi.get(`/courses/${courseId}`);
      setCourse(response.data);
      setImagePreview(response.data.imageUrl);
      setFormData({
        title: response.data.title || "",
        description: response.data.description || "",
        price: response.data.price || "",
        duration: response.data.duration || "",
        level: response.data.level || "",
        status: response.data.status || "active",
        courseSubheading: response.data.courseSubheading || "",
        features: response.data.features || [],
        batchSize: response.data.batchSize || "",
        lessonsCount: response.data.lessonsCount || "",
        durationHours: response.data.durationHours || "",
      });
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load course");
    } finally {
      setLoading(false);
    }
  };

  const loadModules = async () => {
    try {
      // Load modules for batches associated with this course
      const batchesResponse = await adminApi.get(
        `/batches?courseId=${courseId}`
      );
      const batches =
        batchesResponse.data?.batches || batchesResponse.data?.data || [];
      if (batches.length > 0) {
        const batchIds = batches.map((b) => b._id);
        const modulesResponse = await adminApi.get(
          `/modules?batchId=${batchIds[0]}`
        );
        setModules(modulesResponse.data || []);
      }
    } catch (err) {
      console.error("Failed to load modules:", err);
      setModules([]);
    }
  };

  const loadAnalytics = async () => {
    try {
      // Calculate analytics from enrollments and batches
      const batchesResponse = await adminApi.get(
        `/batches?courseId=${courseId}`
      );
      const batches =
        batchesResponse.data?.batches || batchesResponse.data?.data || [];
      const enrollmentsResponse = await adminApi.get(
        `/enrollments?courseId=${courseId}`
      );
      const enrollments =
        enrollmentsResponse.data?.enrollments ||
        enrollmentsResponse.data?.data ||
        [];

      const totalEnrollments = enrollments.length;
      const activeEnrollments = enrollments.filter(
        (e) => e.status === "active"
      ).length;
      const completedEnrollments = enrollments.filter(
        (e) => e.status === "completed"
      ).length;
      const completionRate =
        totalEnrollments > 0
          ? ((completedEnrollments / totalEnrollments) * 100).toFixed(1)
          : 0;

      setAnalytics({
        totalEnrollments,
        activeEnrollments,
        completedEnrollments,
        completionRate,
        totalBatches: batches.length,
        activeBatches: batches.filter((b) => b.status === "active").length,
      });
    } catch (err) {
      console.error("Failed to load analytics:", err);
    }
  };

  const loadReviews = async () => {
    try {
      // If reviews endpoint exists
      const response = await adminApi.get(`/courses/${courseId}/reviews`);
      setReviews(response.data || []);
    } catch (err) {
      // Reviews may not be implemented yet
      setReviews([]);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError("Image size must be less than 5MB");
        e.target.value = ""; // Reset input
        return;
      }

      // Validate file type
      if (!file.type.startsWith("image/")) {
        setError("Please select a valid image file");
        e.target.value = ""; // Reset input
        return;
      }

      setImageFile(file);
      setError(null); // Clear any previous errors
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.onerror = () => {
        setError("Failed to read image file");
        setImageFile(null);
        e.target.value = ""; // Reset input
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDuplicate = async () => {
    try {
      const duplicateData = {
        ...formData,
        title: `${formData.title} (Copy)`,
      };
      const response = await adminApi.post("/courses", duplicateData);
      navigate(`/courses/${response.data._id}`);
      setShowDuplicateModal(false);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to duplicate course");
    }
  };

  const handleSaveAsTemplate = async () => {
    try {
      const templateData = {
        ...formData,
        isTemplate: true,
        templateName:
          prompt("Enter template name:") || `${formData.title} Template`,
      };
      await adminApi.post("/courses/templates", templateData);
      setShowTemplateModal(false);
      alert("Course saved as template successfully!");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save template");
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    try {
      const updateData = { ...formData };

      // Upload image if changed
      if (imageFile) {
        const imageFormData = new FormData();
        imageFormData.append("image", imageFile);
        try {
          const imageResponse = await adminApi.post(
            "/upload/image",
            imageFormData,
            {
              headers: { "Content-Type": "multipart/form-data" },
            }
          );
          updateData.imageUrl =
            imageResponse.data.url || imageResponse.data.imageUrl;
        } catch (imgErr) {
          console.warn("Image upload failed:", imgErr);
        }
      }

      const response = await adminApi.put(
        `/courses/${courseId}`,
        updateData
      );
      setCourse(response.data);
      setImageFile(null);
      // Reset file input
      const fileInput = document.getElementById("edit-course-image");
      if (fileInput) fileInput.value = "";
      setShowEditModal(false);
      loadCourse();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update course");
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await adminApi.delete(`/courses/${courseId}`);
      navigate("/courses");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete course");
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return <div className="detail-loading">Loading course details...</div>;
  }

  if (error && !course) {
    return (
      <div className="detail-error">
        <p>{error}</p>
        <Button onClick={() => navigate("/courses")}>Back to Courses</Button>
      </div>
    );
  }

  return (
    <div className="course-detail-page">
      <header className="detail-header">
        <Link to="/courses" className="back-link">
          <FiArrowLeft size={20} />
          Back to Courses
        </Link>
        <div className="header-actions">
          <Button
            variant="secondary"
            onClick={() => setShowDuplicateModal(true)}
          >
            <FiCopy size={18} />
            Duplicate
          </Button>
          <Button
            variant="secondary"
            onClick={() => setShowTemplateModal(true)}
          >
            <FiFileText size={18} />
            Save as Template
          </Button>
          <Button variant="secondary" onClick={() => setShowVersionModal(true)}>
            <FiFileText size={18} />
            Version History
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              // Reset image state when opening modal
              setImagePreview(course?.imageUrl || null);
              setImageFile(null);
              setShowEditModal(true);
            }}
          >
            <FiEdit size={18} />
            Edit Course
          </Button>
          <Button variant="danger" onClick={() => setShowDeleteModal(true)}>
            <FiTrash2 size={18} />
            Delete
          </Button>
        </div>
      </header>

      <div className="detail-content">
        <div className="detail-main">
          {/* Tabs */}
          <section className="detail-card">
            <div className="tabs">
              <button
                type="button"
                className={`tab ${activeTab === "overview" ? "active" : ""}`}
                onClick={() => setActiveTab("overview")}
              >
                Overview
              </button>
              <button
                type="button"
                className={`tab ${activeTab === "modules" ? "active" : ""}`}
                onClick={() => setActiveTab("modules")}
              >
                <FiLayers size={16} />
                Modules
              </button>
              <button
                type="button"
                className={`tab ${activeTab === "analytics" ? "active" : ""}`}
                onClick={() => setActiveTab("analytics")}
              >
                <FiBarChart2 size={16} />
                Analytics
              </button>
              <button
                type="button"
                className={`tab ${activeTab === "reviews" ? "active" : ""}`}
                onClick={() => setActiveTab("reviews")}
              >
                <FiStar size={16} />
                Reviews
              </button>
            </div>
          </section>

          {/* Overview Tab */}
          {activeTab === "overview" && (
            <section className="detail-card">
              <div className="card-header">
                {imagePreview ? (
                  <div className="course-image-container">
                    <img
                      src={imagePreview}
                      alt={course?.title}
                      className="course-image"
                    />
                  </div>
                ) : (
                  <div className="course-icon">
                    <FiBook size={32} />
                  </div>
                )}
                <div>
                  <h1>{course?.title}</h1>
                  {course?.courseSubheading && (
                    <p className="course-subheading">
                      {course.courseSubheading}
                    </p>
                  )}
                  <StatusBadge status={course?.status || "active"} />
                </div>
              </div>

              <div className="info-section">
                <h2>Course Information</h2>
                <div className="info-grid">
                  <div className="info-item">
                    <FiDollarSign size={18} />
                    <div>
                      <span className="info-label">Price</span>
                      <strong>₹{course?.price || 0}</strong>
                    </div>
                  </div>
                  <div className="info-item">
                    <FiClock size={18} />
                    <div>
                      <span className="info-label">Duration</span>
                      <strong>{course?.duration || "—"}</strong>
                    </div>
                  </div>
                  <div className="info-item">
                    <FiBook size={18} />
                    <div>
                      <span className="info-label">Level</span>
                      <strong>{course?.level || "—"}</strong>
                    </div>
                  </div>
                </div>
              </div>

              {course?.description && (
                <div className="info-section">
                  <h2>Description</h2>
                  <p className="description-text">{course.description}</p>
                </div>
              )}

              {course?.features && course.features.length > 0 && (
                <div className="info-section">
                  <h2>Course Features</h2>
                  <div className="features-list">
                    {course.features.map((feature, index) => (
                      <span key={index} className="feature-tag">
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {course?.prerequisites && course.prerequisites.length > 0 && (
                <div className="info-section">
                  <h2>Prerequisites</h2>
                  <div className="prerequisites-list">
                    {course.prerequisites.map((prereq) => (
                      <Link
                        key={prereq._id}
                        to={`/courses/${prereq._id}`}
                        className="prerequisite-link"
                      >
                        {prereq.title}
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {course?.batches && course.batches.length > 0 && (
                <div className="info-section">
                  <h2>Assigned Batches</h2>
                  <div className="batches-list">
                    {course.batches.map((batch) => (
                      <Link
                        key={batch._id}
                        to={`/batches/${batch._id}`}
                        className="batch-link"
                      >
                        <div>
                          <strong>{batch.batchName}</strong>
                          <span>{batch.status}</span>
                        </div>
                        <FiUsers size={16} />
                        <span>{batch.students?.length || 0} students</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Modules Tab */}
          {activeTab === "modules" && (
            <section className="detail-card">
              <div className="section-header">
                <h2>Course Modules/Curriculum</h2>
                <Button onClick={() => setShowModuleModal(true)}>
                  <FiPlus size={18} />
                  Add Module
                </Button>
              </div>
              {modules.length > 0 ? (
                <Table
                  columns={[
                    { key: "title", label: "Module Title" },
                    { key: "order", label: "Order" },
                    { key: "duration", label: "Duration" },
                    {
                      key: "isOpen",
                      label: "Status",
                      render: (row) => (
                        <StatusBadge
                          status={row.isOpen ? "active" : "closed"}
                        />
                      ),
                    },
                    {
                      key: "actions",
                      label: "Actions",
                      render: (row) => (
                        <div className="module-actions">
                          <Link
                            to={`/modules/${row._id}`}
                            className="action-link"
                          >
                            View
                          </Link>
                        </div>
                      ),
                    },
                  ]}
                  data={modules}
                  emptyMessage="No modules found"
                />
              ) : (
                <p className="empty-state">
                  No modules assigned to this course yet.
                </p>
              )}
            </section>
          )}

          {/* Analytics Tab */}
          {activeTab === "analytics" && (
            <section className="detail-card">
              <h2>Course Analytics</h2>
              {analytics ? (
                <div className="analytics-grid">
                  <div className="analytics-card">
                    <FiUsers size={24} />
                    <div>
                      <span className="analytics-label">Total Enrollments</span>
                      <strong className="analytics-value">
                        {analytics.totalEnrollments}
                      </strong>
                    </div>
                  </div>
                  <div className="analytics-card">
                    <FiActivity size={24} />
                    <div>
                      <span className="analytics-label">
                        Active Enrollments
                      </span>
                      <strong className="analytics-value">
                        {analytics.activeEnrollments}
                      </strong>
                    </div>
                  </div>
                  <div className="analytics-card">
                    <FiTrendingUp size={24} />
                    <div>
                      <span className="analytics-label">Completion Rate</span>
                      <strong className="analytics-value">
                        {analytics.completionRate}%
                      </strong>
                    </div>
                  </div>
                  <div className="analytics-card">
                    <FiLayers size={24} />
                    <div>
                      <span className="analytics-label">Total Batches</span>
                      <strong className="analytics-value">
                        {analytics.totalBatches}
                      </strong>
                    </div>
                  </div>
                  <div className="analytics-card">
                    <FiActivity size={24} />
                    <div>
                      <span className="analytics-label">Active Batches</span>
                      <strong className="analytics-value">
                        {analytics.activeBatches}
                      </strong>
                    </div>
                  </div>
                  <div className="analytics-card">
                    <FiUsers size={24} />
                    <div>
                      <span className="analytics-label">
                        Completed Enrollments
                      </span>
                      <strong className="analytics-value">
                        {analytics.completedEnrollments}
                      </strong>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="empty-state">Loading analytics...</p>
              )}
            </section>
          )}

          {/* Reviews Tab */}
          {activeTab === "reviews" && (
            <section className="detail-card">
              <h2>Course Reviews & Ratings</h2>
              {reviews.length > 0 ? (
                <div className="reviews-list">
                  {reviews.map((review) => (
                    <div key={review._id} className="review-item">
                      <div className="review-header">
                        <div>
                          <strong>
                            {review.user?.firstName} {review.user?.lastName}
                          </strong>
                          <div className="review-rating">
                            {[...Array(5)].map((_, i) => (
                              <FiStar
                                key={i}
                                size={16}
                                fill={i < review.rating ? "#fbbf24" : "none"}
                                color={
                                  i < review.rating ? "#fbbf24" : "#d1d5db"
                                }
                              />
                            ))}
                          </div>
                        </div>
                        <span className="review-date">
                          {new Date(review.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="review-text">{review.comment}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="empty-state">No reviews yet.</p>
              )}
            </section>
          )}
        </div>

        <div className="detail-sidebar">
          <section className="detail-card">
            <h3>Quick Actions</h3>
            <div className="action-list">
              <Button
                variant="secondary"
                fullWidth
                onClick={() => {
                  // Reset image state when opening modal
                  setImagePreview(course?.imageUrl || null);
                  setImageFile(null);
                  setShowEditModal(true);
                }}
              >
                <FiEdit size={16} />
                Edit Course
              </Button>
              <Button
                variant="secondary"
                fullWidth
                onClick={() => setShowDuplicateModal(true)}
              >
                <FiCopy size={16} />
                Duplicate Course
              </Button>
              <Button
                variant="secondary"
                fullWidth
                onClick={() => setShowTemplateModal(true)}
              >
                <FiFileText size={16} />
                Save as Template
              </Button>
              <Button
                variant="secondary"
                fullWidth
                onClick={() => setShowVersionModal(true)}
              >
                <FiFileText size={16} />
                Version History
              </Button>
              <Link
                to={`/batches?courseId=${courseId}`}
                className="action-link"
              >
                <Button variant="secondary" fullWidth>
                  <FiLayers size={16} />
                  View Batches
                </Button>
              </Link>
            </div>
          </section>
        </div>
      </div>

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          // Reset image state when closing modal
          setImagePreview(course?.imageUrl || null);
          setImageFile(null);
          // Reset file input
          const fileInput = document.getElementById("edit-course-image");
          if (fileInput) fileInput.value = "";
        }}
        title="Edit Course"
        size="large"
      >
        <form onSubmit={handleUpdate}>
          <FormInput
            label="Course Title"
            name="title"
            value={formData.title}
            onChange={(e) =>
              setFormData({ ...formData, title: e.target.value })
            }
            required
          />
          <FormInput
            label="Description"
            name="description"
            type="textarea"
            rows={4}
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
          />

          {/* Image Upload in Edit */}
          <div className="image-upload-section">
            <label className="form-input-label">Course Thumbnail/Image</label>
            {imagePreview ? (
              <div className="image-preview-container">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="image-preview"
                />
                <button
                  type="button"
                  className="remove-image-btn"
                  onClick={() => {
                    setImagePreview(course?.imageUrl || null);
                    setImageFile(null);
                  }}
                  title="Remove image"
                >
                  <FiX size={18} />
                </button>
              </div>
            ) : course?.imageUrl ? (
              <div className="image-preview-container">
                <img
                  src={course.imageUrl}
                  alt={course.title}
                  className="image-preview"
                />
              </div>
            ) : (
              <div className="image-upload-placeholder">
                <FiImage size={32} />
                <span>No image uploaded</span>
              </div>
            )}
            <div className="image-upload-controls">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                id="edit-course-image"
                style={{ display: "none" }}
              />
              <label
                htmlFor="edit-course-image"
                className="image-upload-button"
              >
                <FiImage size={18} />
                <span>
                  {imagePreview && imageFile
                    ? "Change Image"
                    : course?.imageUrl
                    ? "Change Image"
                    : "Upload Image"}
                </span>
              </label>
              {imagePreview && imageFile && (
                <button
                  type="button"
                  className="cancel-image-btn"
                  onClick={() => {
                    setImagePreview(course?.imageUrl || null);
                    setImageFile(null);
                    // Reset file input
                    const fileInput =
                      document.getElementById("edit-course-image");
                    if (fileInput) fileInput.value = "";
                  }}
                >
                  <FiX size={18} />
                  Cancel
                </button>
              )}
            </div>
          </div>

          <FormInput
            label="Price (₹)"
            name="price"
            type="number"
            value={formData.price}
            onChange={(e) =>
              setFormData({ ...formData, price: e.target.value })
            }
          />
          <FormInput
            label="Duration"
            name="duration"
            value={formData.duration}
            onChange={(e) =>
              setFormData({ ...formData, duration: e.target.value })
            }
            placeholder="e.g., 6 months"
          />
          <Select
            label="Level"
            name="level"
            value={formData.level}
            onChange={(e) =>
              setFormData({ ...formData, level: e.target.value })
            }
            options={[
              { value: "beginner", label: "Beginner" },
              { value: "intermediate", label: "Intermediate" },
              { value: "advanced", label: "Advanced" },
            ]}
          />
          <Select
            label="Status"
            name="status"
            value={formData.status}
            onChange={(e) =>
              setFormData({ ...formData, status: e.target.value })
            }
            options={[
              { value: "active", label: "Active" },
              { value: "disabled", label: "Disabled" },
            ]}
          />
          <div className="modal-actions">
            <Button
              variant="secondary"
              type="button"
              onClick={() => {
                setShowEditModal(false);
                // Reset image state when canceling
                setImagePreview(course?.imageUrl || null);
                setImageFile(null);
                // Reset file input
                const fileInput = document.getElementById("edit-course-image");
                if (fileInput) fileInput.value = "";
              }}
            >
              Cancel
            </Button>
            <Button type="submit" loading={editLoading}>
              Update Course
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Course"
        size="small"
      >
        <p>Are you sure you want to delete {course?.title}?</p>
        <p className="warning-text">This action cannot be undone.</p>
        <div className="modal-actions">
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            loading={deleteLoading}
          >
            Delete
          </Button>
        </div>
      </Modal>

      {/* Duplicate Course Modal */}
      <Modal
        isOpen={showDuplicateModal}
        onClose={() => setShowDuplicateModal(false)}
        title="Duplicate Course"
        size="small"
      >
        <p>Create a copy of "{course?.title}"?</p>
        <div className="modal-actions">
          <Button
            variant="secondary"
            onClick={() => setShowDuplicateModal(false)}
          >
            Cancel
          </Button>
          <Button onClick={handleDuplicate}>
            <FiCopy size={18} />
            Duplicate
          </Button>
        </div>
      </Modal>

      {/* Save as Template Modal */}
      <Modal
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        title="Save Course as Template"
        size="small"
      >
        <p>Save "{course?.title}" as a reusable template?</p>
        <div className="modal-actions">
          <Button
            variant="secondary"
            onClick={() => setShowTemplateModal(false)}
          >
            Cancel
          </Button>
          <Button onClick={handleSaveAsTemplate}>
            <FiFileText size={18} />
            Save Template
          </Button>
        </div>
      </Modal>

      {/* Module Management Modal */}
      <Modal
        isOpen={showModuleModal}
        onClose={() => setShowModuleModal(false)}
        title="Add Module to Course"
        size="medium"
      >
        <p>Module management will be available through batch management.</p>
        <p className="info-text">
          Modules are created within batches. Please go to the batch page to
          manage modules.
        </p>
        <div className="modal-actions">
          <Button variant="secondary" onClick={() => setShowModuleModal(false)}>
            Close
          </Button>
          <Link to={`/batches?courseId=${courseId}`}>
            <Button>Go to Batches</Button>
          </Link>
        </div>
      </Modal>

      {/* Version History Modal */}
      <Modal
        isOpen={showVersionModal}
        onClose={() => setShowVersionModal(false)}
        title="Course Version History"
        size="large"
      >
        <div className="version-history">
          <p className="info-text">
            Version history tracks changes to course content over time.
          </p>
          <div className="version-list">
            <div className="version-item">
              <div className="version-header">
                <strong>Version 1.0</strong>
                <span className="version-date">
                  {course?.createdAt
                    ? new Date(course.createdAt).toLocaleDateString()
                    : "—"}
                </span>
              </div>
              <p className="version-description">Initial course creation</p>
              <StatusBadge status="current" />
            </div>
            {course?.updatedAt && course.updatedAt !== course.createdAt && (
              <div className="version-item">
                <div className="version-header">
                  <strong>Version 1.1</strong>
                  <span className="version-date">
                    {new Date(course.updatedAt).toLocaleDateString()}
                  </span>
                </div>
                <p className="version-description">Last updated</p>
              </div>
            )}
          </div>
          <p
            className="info-text"
            style={{ marginTop: "1rem", fontSize: "0.875rem" }}
          >
            Note: Full versioning with rollback capabilities requires backend
            support.
          </p>
        </div>
        <div className="modal-actions">
          <Button
            variant="secondary"
            onClick={() => setShowVersionModal(false)}
          >
            Close
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default CourseDetail;
